import { IconInfoCircleOutline } from '@iwoca/icons';
import { Spinner } from '@iwoca/orion';
import { clsx } from 'clsx';

import { ApprovedLimitCell } from './cells/ApprovedLimitCell';
import { CompanyNameCell } from './cells/CompanyNameCell';
import { EmptyValueDash } from './cells/EmptyValueDash';
import { LatestPaymentReferenceLink } from './cells/LatestPaymentReferenceLink';
import { OnboardingProgressCell } from './cells/OnboardingProgressCell';
import { ToSpendCell } from './cells/ToSpendCell';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { formatDate } from '../../../Fees/RateContextHeader/RateContextHeader';
import { CustomerRow, TabConfig } from '../../CustomersV2.types';
import { TableSettings } from '../../hooks/useTableSettings';
import { statusLabel } from '../../statusLabel';
import { STATUS_INFO } from '../StatusInfoModal/statusInfoContent';

const InfoTrigger = ({
  header,
  onInfoClick,
  /** Glyph edge length in px — the two tabs size their icon differently. */
  size = 16,
}: {
  header: string;
  onInfoClick: () => void;
  size?: number;
}) => (
  <button
    type="button"
    aria-label={`About ${header}`}
    onClick={onInfoClick}
    className="flex cursor-pointer items-center border-none bg-transparent p-0 text-primary-10"
  >
    <IconInfoCircleOutline height={size} width={size} aria-hidden />
  </button>
);

export const CustomersMobileTable = ({
  rows,
  loading,
  config,
  settings,
  onCompanyClick = () => {},
  onInfoClick = () => {},
}: {
  rows: CustomerRow[];
  loading: boolean;
  config: TabConfig;
  settings: TableSettings;
  onCompanyClick?: (buyerStateKey: string) => void;
  onInfoClick?: () => void;
}) => {
  if (loading) {
    return (
      <div className="my-2xl flex w-full justify-center">
        <Spinner />
      </div>
    );
  }

  const isOnboarding = config.key === 'onboarding';
  // The modal's title mirrors the column header it explains, and STATUS_INFO
  // covers every tab — so this needs no non-null assertion.
  const infoHeader = STATUS_INFO[config.key].title;

  return (
    // `overflow-hidden` so a row's own background (declined, latest payment)
    // stays inside the rounded corners of the shared frame.
    <div className="overflow-hidden rounded-s border border-secondary-90 bg-structure-100 text-primary-10 shadow-black-s">
      {rows.map((row) => (
        <div
          key={row.buyer_state_key}
          className={clsx(
            'border-b border-secondary-90 last:border-b-0',
            row.application_stage.value === 'declined' && 'bg-core-95',
          )}
        >
          {isOnboarding ? (
            <div className="flex flex-col gap-s p-l">
              <div className="flex items-center justify-between gap-l">
                <span className="text-m font-med">
                  <CompanyNameCell row={row} onCompanyClick={onCompanyClick} />
                </span>
                <span className="text-s font-med">
                  <ApprovedLimitCell row={row} />
                </span>
              </div>
              <div className="flex justify-end text-xs font-reg text-structure-0">
                {formatDate(row.earliest_interacted_date, 'short')}
              </div>
              {/* The stacked cell drops the per-bar tooltips the desktop table
                  explains itself with, so this trigger is the only way to reach
                  the status modal. */}
              <OnboardingProgressCell
                row={row}
                stacked
                infoTrigger={
                  <InfoTrigger header={infoHeader} onInfoClick={onInfoClick} />
                }
              />
            </div>
          ) : (
            // Vertical padding sits here rather than on the children so the
            // latest payment band keeps a 16px gutter below it, while still
            // bleeding to both side edges.
            <div className="flex flex-col gap-l py-l">
              <div className="flex flex-col gap-l px-l">
                <div className="flex items-center justify-between gap-l">
                  <span className="text-xs font-med text-structure-0">
                    {formatDate(row.earliest_interacted_date, 'short')}
                  </span>
                  <div className="flex items-center gap-s text-s font-med text-structure-0">
                    {statusLabel(row)}
                    <InfoTrigger
                      header={infoHeader}
                      onInfoClick={onInfoClick}
                      size={18}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-l">
                  <span className="text-m font-med">
                    <CompanyNameCell
                      row={row}
                      onCompanyClick={onCompanyClick}
                    />
                  </span>
                  <span className="text-s font-med">
                    <ToSpendCell row={row} amountOnly />
                  </span>
                </div>
              </div>
              {settings.latestPayment && (
                <div className="flex items-center justify-between gap-s bg-secondary-95 px-l py-xs text-s">
                  <span className="font-med">Latest payment:</span>
                  {row.latest_payment ? (
                    <div className="flex flex-col items-end gap-2xs">
                      <span className="text-xs font-med">
                        {displayCurrency(row.latest_payment.amount)}
                      </span>
                      <LatestPaymentReferenceLink
                        reference={row.latest_payment.reference}
                      />
                    </div>
                  ) : (
                    <EmptyValueDash
                      row={row}
                      reason="This customer hasn't spent any of their limit yet."
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
